import React from "react";
import { MotiView } from "moti";
import { View, Text, Image, StyleSheet } from "react-native";
import { AnimatedCircularProgress } from "react-native-circular-progress";
import { shadowStyles } from "../../../styles/shadow";
import CustomButton from "../../common/general/CustomButton/CustomButton";

function CourseCard({ title, description, fillPercentage, logoUri, label, onPress }) {
  const progressValue = Number(fillPercentage) || 0;
  const isContinueCourse = progressValue > 0;
  const hasCourseAction = typeof onPress === "function";

  const labelText = String(label ?? "");
  const formattedLabel = labelText.charAt(0).toUpperCase() + labelText.slice(1);

  return (
    <MotiView
      aria-label="school card"
      from={{
        opacity: 0,
        translateY: 20,
      }}
      animate={{
        opacity: 1,
        translateY: 0,
      }}
      delay={1000}
      transition={{ type: "timing", duration: 500 }}>
      <View style={[styles.card, shadowStyles.primary]}>
        <View style={styles.infoContainer}>
          <View>
            <View style={styles.tagContainer}>
              <View style={styles.tag}>
                <Text style={styles.tagText}>{formattedLabel}</Text>
              </View>
            </View>
            <View style={styles.detailsContainer}>
              <Text style={styles.titleText} numberOfLines={2} ellipsizeMode="tail">
                {title}
              </Text>
              <View style={styles.descriptionContainer}>
                <Text style={styles.descriptionText} numberOfLines={2} ellipsizeMode="tail">
                  {description}
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.buttonContainer}>
            {hasCourseAction && (
              <CustomButton
                text={isContinueCourse ? "Continue" : "Start"}
                useTranslationText={false}
                styleType={isContinueCourse ? "primary" : "primaryLightened"}
                size="s"
                bold={true}
                onPress={onPress}
                accessibilityLabel={isContinueCourse ? "Continue course" : "Start course"}
                style={styles.courseButton}
              />
            )}

            <View style={styles.progressContainer}>
              {progressValue > 0 && (
                <View
                  role="progressbar"
                  aria-label="Course progress circle"
                  aria-valuemax={100}
                  aria-valuemin={0}
                  aria-valuenow={progressValue}
                  aria-valuetext={`${progressValue.toFixed(0)}% complete`}>
                  <AnimatedCircularProgress
                    size={45}
                    width={5}
                    fill={progressValue}
                    tintColor="#5BE068"
                    backgroundColor="#E6EBFF"
                    lineCap="round">
                    {() => <Text style={styles.progressText}>{`${progressValue.toFixed(0)}%`}</Text>}
                  </AnimatedCircularProgress>
                </View>
              )}
            </View>

            {logoUri && (
              <Image
                style={styles.progressContainer}
                source={{ uri: logoUri }}
                accessibilityLabel={title}
                alt={description}
                role={"image"}
              />
            )}
          </View>
        </View>
      </View>
    </MotiView>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: 10,
    marginBottom: 20,
    marginHorizontal: 10,
    flex: 1,
    maxWidth: 280,
    minWidth: 280,
    flexDirection: "column",
    padding: 20,
    borderRadius: 10,
    marginVertical: 10,
  },
  infoContainer: {
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    gap: 8,
  },
  tagContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "center",
    marginBottom: 8,
  },
  tag: {
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    backgroundColor: "#E6EBFF",
    borderRadius: 100,
    alignContent: "center",
  },
  tagText: {
    color: "#022CC4",
    fontSize: 10,
    fontWeight: "700",
    wordWrap: "break-word",
  },
  detailsContainer: {
    alignSelf: "stretch",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    display: "flex",
  },
  titleText: {
    alignSelf: "stretch",

    color: "#2D2F36",
    fontSize: 24,
    fontWeight: "700",
    wordWrap: "break-word",
  },
  descriptionContainer: {
    alignSelf: "stretch",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 10,
    display: "flex",
    marginTop: 8,
  },
  descriptionText: {
    alignSelf: "stretch",
    color: "#4B2359",
    fontSize: 14,
    fontWeight: "400",
    wordWrap: "break-word",
  },
  buttonContainer: {
    flexDirection: "row",
    alignSelf: "stretch",
    justifyContent: "space-between",
    alignItems: "flex-start",
    display: "inline-flex",
  },
  courseButton: {
    padding: 12,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
    display: "flex",
  },
  progressContainer: {
    width: 44,
    height: 44,
  },
  progressText: {
    fontSize: 10,
    fontWeight: "700",
  },
});

export default CourseCard;
